import { useState } from "react";
import { AlertCircle, Check, KeyRound, ShieldCheck, User2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminSettings() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const needsPasswordChange = !user?.password_changed_at;

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (next !== confirm) {
      setErr("New password fields don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/auth/me/password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setConfirm("");
      await refresh();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.message || "Update failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <header className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Settings
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Super-admin profile and credentials.
          </p>
        </div>
      </header>

      {needsPasswordChange && (
        <div
          className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
          data-testid="admin-settings-changepw-banner"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong className="text-amber-300">
              Please change your default seeded password.
            </strong>{" "}
            The super-admin account still has the initial password set in{" "}
            <code>SEED_SUPERADMIN_PASSWORD</code>.
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-200">
            <User2 className="h-4 w-4 text-amber-400" /> Profile
          </div>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Name</dt>
              <dd className="text-slate-100" data-testid="admin-settings-name">{user?.name}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Email</dt>
              <dd className="font-mono text-slate-100" data-testid="admin-settings-email">{user?.email}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Role</dt>
              <dd>
                <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-xs text-amber-300">
                  Super admin
                </span>
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Password changed</dt>
              <dd className="text-slate-100">
                {user?.password_changed_at
                  ? new Date(user.password_changed_at).toLocaleString()
                  : "Never (seeded)"}
              </dd>
            </div>
          </dl>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-200">
            <KeyRound className="h-4 w-4 text-amber-400" /> Change password
          </div>
          <form onSubmit={submit} className="space-y-3" data-testid="admin-settings-changepw-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-400" htmlFor="admin-pw-cur">
                Current password
              </label>
              <input
                id="admin-pw-cur"
                type="password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="admin-settings-pw-current"
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-400" htmlFor="admin-pw-new">
                New password
              </label>
              <input
                id="admin-pw-new"
                type="password"
                required
                value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="admin-settings-pw-new"
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                At least 8 characters, mix letters + a digit.
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-400" htmlFor="admin-pw-conf">
                Confirm new password
              </label>
              <input
                id="admin-pw-conf"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="admin-settings-pw-confirm"
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
              />
            </div>
            {err && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-settings-pw-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
            >
              <Check className="h-4 w-4" /> Update password
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
